import type { ApiErrorBody, ErrorCode, FieldError } from '@whn/shared';

export const API_BASE = `${import.meta.env.VITE_API_URL ?? 'http://localhost:4000'}/api/v1`;

const CSRF_COOKIE = 'whn_csrf';
const CSRF_HEADER = 'x-csrf-token';

/** A structured API failure, with the field errors the forms need. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: FieldError[];
  readonly requestId?: string;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = body.statusCode;
    this.code = body.code;
    this.details = body.details ?? [];
    this.requestId = body.requestId;
  }

  /** Field errors keyed by path, ready to hand to react-hook-form. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.details.map((detail) => [detail.path, detail.message]));
  }
}

/** Thrown when the network never reached the API at all. */
/**
 * Thrown when `fetch` itself rejects — the request never produced a response.
 *
 * The message names the API's address and both plausible causes, because the
 * browser deliberately hides which one it was. A blocked cross-origin response
 * and an unreachable server are indistinguishable from JavaScript: both arrive
 * as the same bare TypeError.
 *
 * The old message ("check your connection") was confidently wrong for the CORS
 * case and cost real debugging time — the connection was fine, the page was
 * just served from an origin the API's CORS_ORIGINS did not list. Naming the
 * URL makes that mismatch visible at a glance.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(
      `Could not reach the API at ${API_BASE}. It may not be running, or this page's address may not be one it accepts.`,
    );
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1];
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set on the refresh call itself, to stop it recursing into itself. */
  skipAuthRetry?: boolean;
}

/** Refreshing is what recovers a 401; a 401 from it cannot be retried. */
const REFRESH_PATH = '/auth/refresh';

/**
 * A single in-flight refresh shared by every caller. Without this, five
 * parallel queries hitting an expired token would fire five refreshes — and
 * because refresh tokens rotate, four of them would look like token reuse and
 * revoke the whole session.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      await rawRequest(REFRESH_PATH, { method: 'POST', skipAuthRetry: true });
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers all observe the same
      // promise before it is reset.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

/** Listeners notified when the session is definitively gone. */
const sessionExpiryHandlers = new Set<() => void>();

export function onSessionExpired(handler: () => void): () => void {
  sessionExpiryHandlers.add(handler);
  return () => sessionExpiryHandlers.delete(handler);
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  // FormData sets its own Content-Type, including the multipart boundary the
  // browser generates. Setting the header ourselves would omit that boundary
  // and every upload would fail to parse.
  const isFormData = options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  // Double-submit: echo the readable CSRF cookie in a header a cross-origin
  // page cannot set.
  if (method !== 'GET') {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      // Cookies are the credential; they must ride on every request.
      credentials: 'include',
      body:
        options.body === undefined
          ? undefined
          : isFormData
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(
      (payload as ApiErrorBody | undefined) ?? {
        statusCode: response.status,
        code: 'INTERNAL_ERROR',
        message: response.statusText || 'Request failed',
        path,
        timestamp: new Date().toISOString(),
      },
    );
  }

  return payload as T;
}

/**
 * The client every feature uses. On a 401 it transparently refreshes the
 * session once and retries; if that fails, listeners are told the session is
 * over so the app can route to sign-in.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (error) {
    const isExpiredSession =
      error instanceof ApiError &&
      error.status === 401 &&
      !options.skipAuthRetry &&
      // Guard the path itself, not just the flag: a caller reaching for
      // /auth/refresh directly would otherwise recurse until the stack gave up.
      path !== REFRESH_PATH;
    if (!isExpiredSession) throw error;

    const refreshed = await refreshSession();
    if (!refreshed) {
      for (const handler of sessionExpiryHandlers) handler();
      throw error;
    }
    return rawRequest<T>(path, { ...options, skipAuthRetry: true });
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  /** Multipart upload. Goes through the same auth retry as everything else. */
  upload: <T>(path: string, files: readonly File[], field = 'files') => {
    const form = new FormData();
    for (const file of files) form.append(field, file);
    return apiRequest<T>(path, { method: 'POST', body: form });
  },
};

/**
 * Fetches a file and hands it to the browser as a download.
 *
 * Not a plain `<a href>`: the endpoint needs the session cookie *and* returns
 * a JSON error body when it refuses, which a navigation would render as a page
 * of JSON instead of surfacing as an error the UI can show. Fetching it means
 * a 403 arrives as an ApiError like every other failure.
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  /** Present for endpoints that take parameters too large for a query string. */
  body?: unknown,
): Promise<void> {
  const headers: Record<string, string> = { Accept: '*/*' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  }).catch((cause: unknown) => {
    throw new NetworkError(cause);
  });

  if (!response.ok) {
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as ApiErrorBody) : undefined;
    throw new ApiError(
      payload ?? {
        statusCode: response.status,
        code: 'INTERNAL_ERROR',
        message: response.statusText || 'Download failed',
        path,
        timestamp: new Date().toISOString(),
      },
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFrom(response.headers.get('content-disposition')) ?? fallbackName;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in some browsers; a tick is
  // enough for the click to have been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Opens a PDF's authenticated "view inline" endpoint in a new tab.
 *
 * A plain navigation rather than a fetch-and-blob dance like `downloadFile`:
 * the endpoint sets `Content-Disposition: inline`, so the browser renders it
 * in the tab itself. That works precisely because it *is* a navigation — the
 * session cookie is `SameSite=Lax`, which rides along on a top-level GET like
 * this one even across the API's own origin, and the request never needs a
 * CSRF header because GET is exempt from that check.
 */
export function openFileInline(path: string): void {
  window.open(`${API_BASE}${path}`, '_blank', 'noopener');
}

/**
 * Downloads a file by navigating to it rather than fetching it as a blob.
 *
 * `downloadFile` reads the whole response into memory first, which is fine
 * for a PDF but not for a backup part that can run to hundreds of megabytes —
 * the browser's own download manager streams a navigation to disk without
 * ever holding it all in the page's memory. Works for the same reason
 * `openFileInline` does: a same-site `Lax` cookie rides along on a top-level
 * navigation, and the server's `Content-Disposition: attachment` is what
 * makes the browser save it instead of trying to display it.
 */
export function downloadFileByNavigation(path: string): void {
  window.open(`${API_BASE}${path}`, '_blank', 'noopener');
}

/**
 * Posts a body and opens the PDF that comes back inline, in a new tab —
 * `openFileInline`'s job, but for an endpoint that has to take a request body
 * (a template draft too large, and too transient, to belong in a query
 * string) rather than a plain GET.
 *
 * The tab is opened synchronously, before the `fetch` even starts, and only
 * pointed at the result afterwards. A browser's popup blocker allows a
 * `window.open` call made directly inside a click handler but not one made
 * after an `await` — by the time the response arrives, the call stack no
 * longer looks user-initiated. Opening a blank tab immediately and
 * redirecting it once the blob is ready sidesteps that without needing the
 * user to approve a popup for an action they, in fact, just took.
 *
 * Deliberately without `noopener`: that flag makes `window.open` return
 * `null` (it isolates the new tab from the opener in both directions, not
 * just one), which would leave nothing to redirect once the blob is ready.
 * What ends up loaded in the tab is a same-origin `blob:` URL serving a PDF —
 * never a document with script of its own — so there is nothing for a
 * retained opener reference to be misused by.
 */
export async function viewFileInlineFromPost(path: string, body: unknown): Promise<void> {
  const tab = window.open('', '_blank');

  try {
    const headers: Record<string, string> = {
      Accept: 'application/pdf',
      'Content-Type': 'application/json',
    };
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;

    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    }).catch((cause: unknown) => {
      throw new NetworkError(cause);
    });

    if (!response.ok) {
      const text = await response.text();
      const payload = text ? (JSON.parse(text) as ApiErrorBody) : undefined;
      throw new ApiError(
        payload ?? {
          statusCode: response.status,
          code: 'INTERNAL_ERROR',
          message: response.statusText || 'Could not render that preview',
          path,
          timestamp: new Date().toISOString(),
        },
      );
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = url;
    } else {
      // The blank tab never opened — a stricter popup blocker, most likely.
      // Falling back to a fresh `window.open` here is still inside the same
      // user gesture's microtask chain often enough to work, and is strictly
      // better than silently doing nothing.
      window.open(url, '_blank', 'noopener');
    }
    // Revoked well after either tab has had time to load the PDF from it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** Reads the server's chosen filename, ignoring anything path-like in it. */
function filenameFrom(header: string | null): string | undefined {
  const match = header?.match(/filename="([^"]+)"/u);
  const name = match?.[1];
  return name ? name.split(/[\\/]/u).pop() : undefined;
}

/** Serialises a filter object into a query string, dropping empty values. */
export function toQueryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
